import { Button, Icons, Sheet, Stack, Typography } from '@healthinal/ui';
import { useAtom } from 'jotai';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { AssignedToFilter } from '../../api/assignedToFilter.ts';
import { GetPatientsSummaryParams } from '../../api/generated/model';
import { useGetPatientsSummary } from '../../api/generated/summary-controller/summary-controller.ts';
import { ContentSection } from '../../components/ContentSection.tsx';
import {
  patientAssignedEmployeeIdsAtom,
  patientAssignedToAtom,
  patientSearchTextAtom,
  patientTableActiveOnly,
  patientTableFiltersAtom,
  PatientTableView,
  patientTableViewAtom,
} from '../../state/patientSearchAtoms.ts';
import { filterPatientsForTable } from '../../utils/patientSearch/patientTableFilter.ts';
import { PatientsContactTracingTable } from '../patients/components/tables/PatientsContactTracingTable.tsx';
import { PatientsFindingsTable } from '../patients/components/tables/PatientsFindingsTable.tsx';
import { PatientsScreeningTable } from '../patients/components/tables/PatientsScreeningTable.tsx';
import { PatientsTherapyTable } from '../patients/components/tables/PatientsTherapyTable.tsx';
import { PatientTableSearch } from '../patients/components/tables/PatientTableSearch.tsx';

export function PatientOverview({
  title,
  tableViews,
  createCaseButtonLabel,
  renderCreateDialog,
}: {
  title: string;
  tableViews: PatientTableView[];
  createCaseButtonLabel: string;
  renderCreateDialog: (props: { open: boolean; onClose: () => void }) => ReactNode;
}) {
  const [activeOnly] = useAtom(patientTableActiveOnly);
  const [isCreateCaseOpen, setIsCreateCaseOpen] = useState(false);

  const [searchText] = useAtom(patientSearchTextAtom);
  const [selectedTable, setSelectedTable] = useAtom(patientTableViewAtom);
  const [selectedFilters] = useAtom(patientTableFiltersAtom);
  const [assignedTo] = useAtom(patientAssignedToAtom);
  const [assignedEmployeeIds] = useAtom(patientAssignedEmployeeIdsAtom);
  const [page, setPage] = useState(0);
  const [size] = useState(20);

  const availableTableViews = useMemo(() => new Set(tableViews), [tableViews]);
  const initialTableView = tableViews[0];
  const activeTableView = availableTableViews.has(selectedTable) ? selectedTable : initialTableView;
  const search = searchText.trim();
  const assignedEmployeeIdsParam = useMemo(() => Array.from(assignedEmployeeIds).join(','), [assignedEmployeeIds]);

  useEffect(() => {
    if (!availableTableViews.has(selectedTable)) {
      setSelectedTable(initialTableView);
    }
  }, [availableTableViews, initialTableView, selectedTable, setSelectedTable]);

  useEffect(() => {
    setPage(0);
  }, [activeOnly, activeTableView, assignedEmployeeIdsParam, assignedTo, search, selectedFilters]);

  const summaryParams: GetPatientsSummaryParams = useMemo(() => {
    const paginationParams = {
      page,
      size,
      ...(search ? { search } : {}),
    };

    if (activeTableView === PatientTableView.SCREENING) {
      return { activeOnly, assignedTo: AssignedToFilter.ALL, ...paginationParams };
    }

    return {
      activeOnly,
      assignedTo,
      ...(assignedTo === AssignedToFilter.SPECIFIC && assignedEmployeeIdsParam
        ? { assignedEmployeeIds: assignedEmployeeIdsParam }
        : {}),
      ...paginationParams,
    };
  }, [activeOnly, activeTableView, assignedEmployeeIdsParam, assignedTo, page, search, size]);

  const { data: patientsPage, isFetching } = useGetPatientsSummary(summaryParams);
  const patients = patientsPage?.content ?? [];
  const totalCount = patientsPage?.totalCount ?? 0;
  const currentPage = patientsPage?.page ?? page;
  const currentSize = patientsPage?.size ?? size;
  const totalPages = currentSize > 0 ? Math.ceil(totalCount / currentSize) : 0;
  const filteredPatients = useMemo(
    () => filterPatientsForTable(patients, activeTableView, [...selectedFilters]),
    [activeTableView, patients, selectedFilters],
  );

  const handlePrevPage = () => setPage((prev) => Math.max(0, prev - 1));
  const handleNextPage = () => setPage((prev) => Math.min(Math.max(0, totalPages - 1), prev + 1));

  const tableScrollRef = useRef<HTMLDivElement>(null);

  return (
    <>
      <ContentSection gap={2} sx={{ overflow: 'hidden', minWidth: 0 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography level="h1">{title}</Typography>
          <Button onClick={() => setIsCreateCaseOpen(true)} endDecorator={<Icons.Add />}>
            {createCaseButtonLabel}
          </Button>
        </Stack>
        <PatientTableSearch tableViews={tableViews} selectedTable={activeTableView} />
        <Sheet
          sx={{
            px: 2,
            py: 1,
            borderRadius: 5,
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}>
          <Sheet ref={tableScrollRef} sx={{ flex: 1, minWidth: 0, minHeight: 0, overflow: 'auto', width: '100%' }}>
            {activeTableView === PatientTableView.FINDINGS && (
              <PatientsFindingsTable patients={filteredPatients} parentRef={tableScrollRef} />
            )}
            {activeTableView === PatientTableView.THERAPY && (
              <PatientsTherapyTable patients={filteredPatients} parentRef={tableScrollRef} />
            )}
            {activeTableView === PatientTableView.CONTACT_TRACING && (
              <PatientsContactTracingTable patients={filteredPatients} parentRef={tableScrollRef} />
            )}
            {activeTableView === PatientTableView.SCREENING && (
              <PatientsScreeningTable patients={filteredPatients} parentRef={tableScrollRef} />
            )}
          </Sheet>
          <PatientOverviewPagination
            isLoading={isFetching}
            page={currentPage}
            totalPages={totalPages}
            onPrevPage={handlePrevPage}
            onNextPage={handleNextPage}
            disablePrev={currentPage === 0 || isFetching}
            disableNext={currentPage + 1 >= totalPages || isFetching}
          />
        </Sheet>
      </ContentSection>
      {renderCreateDialog({
        open: isCreateCaseOpen,
        onClose: () => setIsCreateCaseOpen(false),
      })}
    </>
  );
}

function PatientOverviewPagination({
  isLoading,
  page,
  totalPages,
  onPrevPage,
  onNextPage,
  disablePrev,
  disableNext,
}: {
  isLoading: boolean;
  page: number;
  totalPages: number;
  onPrevPage: () => void;
  onNextPage: () => void;
  disablePrev?: boolean;
  disableNext?: boolean;
}) {
  const visiblePage = totalPages === 0 ? 0 : page + 1;
  const [isPaginationPending, setIsPaginationPending] = useState(false);
  const isPaginationPendingRef = useRef(false);
  const isPrevDisabled = disablePrev || isPaginationPending;
  const isNextDisabled = disableNext || isPaginationPending;

  useEffect(() => {
    if (!isLoading) {
      isPaginationPendingRef.current = false;
      setIsPaginationPending(false);
    }
  }, [isLoading, page]);

  const handlePrevPage = () => {
    if (isPrevDisabled || isPaginationPendingRef.current) return;

    isPaginationPendingRef.current = true;
    setIsPaginationPending(true);
    onPrevPage();
  };

  const handleNextPage = () => {
    if (isNextDisabled || isPaginationPendingRef.current) return;

    isPaginationPendingRef.current = true;
    setIsPaginationPending(true);
    onNextPage();
  };

  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
      <Typography level="body-sm">
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
          }}>
          <span>Seite</span>
          <span translate="no" className="notranslate">
            {visiblePage}
          </span>
          <span>von</span>
          <span translate="no" className="notranslate">
            {totalPages}
          </span>
        </span>
      </Typography>

      <Stack direction="row" spacing={1}>
        <Button variant="outlined" disabled={isPrevDisabled} onClick={handlePrevPage}>
          Zurück
        </Button>

        <Button variant="outlined" disabled={isNextDisabled} onClick={handleNextPage}>
          Weiter
        </Button>
      </Stack>
    </Stack>
  );
}
